import { useState, useEffect, useMemo, useCallback } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, RotateCcw, Undo2, History, ShieldAlert } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useGetConfigurationSettings,
  useUpdateConfigurationSettings,
  useGetConfigurationSettingChanges,
} from "@/resources/configuration-settings/configuration-settings.api";
import { groupChangesIntoEntries } from "./kps-configuration/utils";
import { BRAND_INTELLIGENCE_ALIASES } from "./kps-configuration/constants";
import { ChangeHistoryDialog } from "./kps-configuration/ChangeHistoryDialog";

// AUT-245 section 7: Brand Intelligence configuration parameters. Aliases mirror
// backend DefaultLandscapeConfiguration + UpdateConfigurationSettingsRequest::VALUE_BOUNDS.
type BiParam = {
  alias: string;
  label: string;
  description: string;
  default: string;
  step: number;
};

type BiSection = { title: string; description?: string; params: BiParam[] };

const BI_SECTIONS: BiSection[] = [
  {
    title: "Classification & Alerts",
    params: [
      {
        alias: "bi_asp_band_pct",
        label: "ASP Band Width (%)",
        description:
          "Competitors priced within this percentage of the brand's category price are tagged Competitive, otherwise Broad.",
        default: "25",
        step: 1,
      },
      {
        alias: "bi_hero_concentration_pct",
        label: "Hero ASIN Concentration Alert (%)",
        description:
          "Flag a category when the top parent ASIN's revenue exceeds this share of the brand's category revenue.",
        default: "60",
        step: 1,
      },
    ],
  },
  {
    title: "Growth & Trend",
    params: [
      {
        alias: "bi_growth_threshold_pp",
        label: "Growth Status Threshold (pp)",
        description:
          "Outpacing / Lagging when brand growth differs from category growth by more than this many percentage points.",
        default: "5",
        step: 1,
      },
      {
        alias: "bi_trend_deadband_pp",
        label: "Trend Dead-band (pp)",
        description: "Month-over-month SOV moves smaller than this display as flat.",
        default: "0.3",
        step: 0.1,
      },
      {
        alias: "bi_large_gap_pp",
        label: "Large SOV Gap Threshold (pp)",
        description: "A category counts toward the Competitors KPI when the SOV gap to the #1 competitor exceeds this.",
        default: "5",
        step: 1,
      },
    ],
  },
  {
    title: "Category Health Weights",
    description: "Composite health score = (SOV × SOV weight) + (Rev share × Rev-share weight) + (growth diff × Growth-diff weight).",
    params: [
      {
        alias: "bi_health_weight_sov",
        label: "Weight — SOV",
        description: "SOV multiplier in the category health composite score.",
        default: "40",
        step: 1,
      },
      {
        alias: "bi_health_weight_revshare",
        label: "Weight — Revenue Share",
        description: "Revenue-share multiplier in the category health composite score.",
        default: "2",
        step: 1,
      },
      {
        alias: "bi_health_weight_growth",
        label: "Weight — Growth Diff",
        description: "Growth-difference multiplier (in percentage points) in the category health composite score.",
        default: "0.3",
        step: 0.1,
      },
    ],
  },
  {
    title: "Catalog & Refresh",
    params: [
      {
        alias: "bi_full_catalog_bsr_threshold",
        label: "Full Catalog BSR Threshold",
        description:
          "ASINs qualify for the brand's Full Catalog when their root-category BSR is below this value.",
        default: "700000",
        step: 1000,
      },
      {
        alias: "bi_refresh_cooldown_hours",
        label: "Refresh Cooldown (hours)",
        description:
          "Minimum time between Brand Intelligence rebuilds. Users can bypass it with an explicit acknowledgment.",
        default: "24",
        step: 1,
      },
    ],
  },
];

const ALL_PARAMS: BiParam[] = BI_SECTIONS.flatMap((s) => s.params);
const DEFAULTS: Record<string, string> = Object.fromEntries(ALL_PARAMS.map((p) => [p.alias, p.default]));

function isValidNumericInput(value: string): boolean {
  if (value.trim() === "") return false;
  const n = Number(value);
  return Number.isFinite(n) && n >= 0;
}

export function BrandIntelligenceTab() {
  const { data: configData, isLoading } = useGetConfigurationSettings();
  const updateMutation = useUpdateConfigurationSettings();
  const { data: changeHistory, isLoading: isChangesLoading } = useGetConfigurationSettingChanges();

  const [values, setValues] = useState<Record<string, string>>({ ...DEFAULTS });
  const [hasChanges, setHasChanges] = useState(false);
  const [initialized, setInitialized] = useState(false);
  const [undoStack, setUndoStack] = useState<Record<string, string>[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    if (!configData || initialized) return;
    const map: Record<string, string> = {};
    for (const s of configData) map[s.alias] = s.value;
    setValues((prev) => {
      const next = { ...prev };
      for (const p of ALL_PARAMS) {
        const raw = map[p.alias];
        next[p.alias] = raw !== undefined && !Number.isNaN(Number(raw)) ? String(Number(raw)) : p.default;
      }
      return next;
    });
    setInitialized(true);
  }, [configData, initialized]);

  const pushUndo = useCallback(() => {
    setUndoStack((prev) => [...prev.slice(-49), { ...values }]);
  }, [values]);

  const handleChange = (alias: string, value: string) => {
    pushUndo();
    setValues((prev) => ({ ...prev, [alias]: value }));
    setHasChanges(true);
  };

  const handleUndo = () => {
    if (undoStack.length === 0) return;
    const prev = undoStack[undoStack.length - 1];
    setUndoStack((s) => s.slice(0, -1));
    setValues(prev);
    setHasChanges(true);
    toast("Change undone", { description: "Reverted to the previous value." });
  };

  const handleReset = () => {
    pushUndo();
    setValues({ ...DEFAULTS });
    setHasChanges(true);
  };

  const isValid = ALL_PARAMS.every((p) => isValidNumericInput(values[p.alias] ?? ""));

  const handleSave = () => {
    updateMutation.mutate(
      { settings: ALL_PARAMS.map((p) => ({ alias: p.alias, value: String(Number(values[p.alias])) })) },
      {
        onSuccess: () => {
          toast.success("Configuration Saved", { description: "Brand Intelligence settings have been updated." });
          setHasChanges(false);
          setUndoStack([]);
        },
      },
    );
  };

  // AUT-362: the change log is shared with KPS Configuration, so show only Brand Intelligence settings here.
  const changeLogEntries = useMemo(
    () => (changeHistory ? groupChangesIntoEntries(changeHistory.filter((c) => BRAND_INTELLIGENCE_ALIASES.has(c.alias))) : []),
    [changeHistory],
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-sm text-muted-foreground">Loading configuration...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {hasChanges && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 border border-yellow-300 text-yellow-700 text-xs font-medium">
          <ShieldAlert className="h-3.5 w-3.5 shrink-0" />
          You have unsaved changes
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5 rounded-lg bg-card"
          onClick={() => setHistoryOpen(true)}
        >
          <History className="h-3.5 w-3.5" />
          Change History
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5 rounded-lg bg-card"
          onClick={handleUndo}
          disabled={undoStack.length === 0}
        >
          <Undo2 className="h-3.5 w-3.5" />
          Undo
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="h-8 text-xs gap-1.5 rounded-lg bg-card"
          onClick={handleReset}
        >
          <RotateCcw className="h-3.5 w-3.5" />
          Reset to Default
        </Button>
        <Button
          size="sm"
          className={cn("h-8 text-xs gap-1.5", (!hasChanges || !isValid) && "bg-muted text-muted-foreground hover:bg-muted")}
          onClick={handleSave}
          disabled={!hasChanges || !isValid || updateMutation.isPending}
        >
          <Save className="h-3.5 w-3.5" />
          Save Configuration
        </Button>
      </div>

      {BI_SECTIONS.map((section) => (
        <Card key={section.title}>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">{section.title}</CardTitle>
            {section.description && (
              <p className="text-xs text-muted-foreground mt-1">{section.description}</p>
            )}
          </CardHeader>
          <CardContent className="pt-2 space-y-6">
            {section.params.map((p) => {
              const value = values[p.alias] ?? "";
              const invalid = !isValidNumericInput(value);
              return (
                <div key={p.alias} className="space-y-1.5">
                  <Label htmlFor={`bi-${p.alias}`} className="text-sm font-medium">
                    {p.label}
                  </Label>
                  <p className="text-xs text-muted-foreground">{p.description}</p>
                  <Input
                    id={`bi-${p.alias}`}
                    type="number"
                    min={0}
                    step={p.step}
                    value={value}
                    onChange={(e) => handleChange(p.alias, e.target.value)}
                    className={cn(
                      "w-40 h-9 rounded-lg bg-card tabular-nums",
                      invalid && "border-status-error-mid focus-visible:ring-status-error-mid",
                    )}
                  />
                </div>
              );
            })}
          </CardContent>
        </Card>
      ))}

      <ChangeHistoryDialog
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        isLoading={isChangesLoading}
        entries={changeLogEntries}
        description="A log of every Brand Intelligence configuration change — thresholds, health weights, and refresh settings."
      />
    </div>
  );
}
